/**
 * RPC 실패의 **분류.** 정책은 여기 없다. (docs/decisions/0008 §401 의 소유자)
 *
 * ─── 왜 분류와 정책을 나누는가 ───
 * 401 은 사용자가 앉아 있는 화면의 in-flight 뮤테이션에서 온다. 무엇을 할지는
 * 그 화면의 상태에 달렸다(폼이 더러운가, 신원이 바뀌었는가). 통신 계층이 그 결정을
 * 내리면 "저장 중이던 입력을 날리는" 코드가 통신 계층에 생긴다.
 * 그래서 여기서는 **무엇이 일어났는지만** 말하고, `<SessionWatcher>`(core-web)가 정책을 실행한다.
 *
 * ─── 왜 문자열 매칭이 아니라 code 인가 ───
 * 서버는 에러 레지스트리의 `(status, code)` 를 계약으로 내보낸다(`packages/contracts/errors.ts`).
 * `message` 에는 **i18n 키**가 들어 있고 렌더된 문장이 아니다 — 클라이언트가 문장으로 분기하기
 * 시작하면 로케일을 바꾸는 순간 분기가 조용히 죽는다.
 */
import { ORPCError } from '@orpc/client';
import { ERRORS, type ErrorCode } from '@worhythm/contracts';

/**
 * 실패의 종류. **`offline` 이 따로 있는 이유**: 응답 자체가 없는 것과 서버가 거부한 것은
 * 사용자에게 할 말이 정반대다("다시 시도" vs "권한을 요청"). 하나로 접으면 오프라인 사용자에게
 * 권한 요청 안내가 뜬다.
 */
export type RpcFailureKind = 'unauthenticated' | 'forbidden' | 'app' | 'offline' | 'unknown';

export interface RpcFailure {
  readonly kind: RpcFailureKind;
  /** 에러 레지스트리의 code. 서버가 준 것이 레지스트리에 없으면 `undefined` 다 — 지어내지 않는다. */
  readonly code: ErrorCode | undefined;
  readonly status: number | undefined;
  /** 렌더할 i18n 키. 서버가 준 것을 먼저 쓰고, 없으면 레지스트리에서 찾는다. */
  readonly messageKey: string;
  readonly nextActionKey: string | undefined;
  readonly retryable: boolean;
  /**
   * 서버가 실은 `details`. **화면이 분기하는 자리다** — 예: `validation.failed` 의
   * `{field:'offset'}` 은 "폼을 고치세요"가 아니라 "페이지 번호가 범위를 벗어났다"이고,
   * 그 둘은 사용자가 할 수 있는 일이 완전히 다르다(하나는 입력 수정, 하나는 첫 페이지로).
   * 레지스트리의 문구만으로는 그 구분이 안 되므로 화면이 여기를 본다.
   *
   * 타입이 `unknown` 인 이유: 모양이 에러마다 다르고, 여기서 유니온으로 좁히면
   * **에러 레지스트리의 두 번째 정의**가 된다. 좁히는 것은 읽는 화면의 몫이다.
   */
  readonly details: unknown;
  /** 원본. 로깅·디버깅용이고 화면에 그대로 그리지 않는다(스택·SQL 이 새는 자리다). */
  readonly cause: unknown;
}

/** `details` 에서 필드 이름 하나를 꺼낸다. 모양이 다르면 `undefined` — 지어내지 않는다. */
export function failedField(failure: RpcFailure): string | undefined {
  const d = failure.details;
  if (typeof d !== 'object' || d === null) return undefined;
  const f = (d as Record<string, unknown>).field;
  return typeof f === 'string' ? f : undefined;
}

/**
 * `details.messageKey` 를 꺼낸다. 모양이 다르면 `undefined` — 지어내지 않는다.
 *
 * 서버가 문장이 아니라 **키**를 싣는 규약의 클라이언트 쪽 절반이다(급여 blocker ·
 * 근태 계산 오류 · **급여 조정의 전이 거절**이 같은 자리를 쓴다). 렌더는 화면이 하므로
 * 웹·모바일·PDF 가 같은 사실을 각자의 언어로 그릴 수 있다.
 *
 * 조정이 이 규약을 쓰는 이유가 그 필요를 잘 보여 준다: 전이 거절은 전부
 * `resource.conflict` 인데 이유가 셋이고(확정 전 지급 · 이미 확정 · 이미 지급) 셋의
 * 다음 행동이 다르다. 이유마다 code 를 만들면 `(status, code)` 가 API 계약이라
 * (AGENTS.md 16) 문구를 다듬을 때마다 계약이 늘어난다.
 */
export function failedMessageKey(failure: RpcFailure): string | undefined {
  const d = failure.details;
  if (typeof d !== 'object' || d === null) return undefined;
  const k = (d as Record<string, unknown>).messageKey;
  return typeof k === 'string' ? k : undefined;
}

/**
 * `details.dates` 를 꺼낸다 — **어느 날짜가 문제인가.** 모양이 다르면 빈 배열이다.
 *
 * `failedMessageKey` 와 같은 규약의 세 번째 조각이다: 서버는 문장이 아니라 **사실**을
 * 싣고 화면이 렌더한다. 이 칸이 필요한 이유는 휴가 승인이 근태 충돌로 거절될 때인데,
 * 구 시스템은 그때 23505 로 죽어서 사용자에게는 "승인 버튼이 안 먹는다"였다 —
 * 어느 날인지 말하지 않으면 결재자가 고칠 자리를 찾을 수 없다.
 */
export function failedDates(failure: RpcFailure): string[] {
  const d = failure.details;
  if (typeof d !== 'object' || d === null) return [];
  const v = (d as Record<string, unknown>).dates;
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

function isErrorCode(code: string): code is ErrorCode {
  return Object.hasOwn(ERRORS, code);
}

function stringField(data: unknown, field: string): string | undefined {
  if (typeof data !== 'object' || data === null) return undefined;
  const v = (data as Record<string, unknown>)[field];
  return typeof v === 'string' ? v : undefined;
}

/**
 * 무엇이 던져졌든 하나의 모양으로 만든다.
 *
 * `fetch` 가 던지는 `TypeError` 는 응답이 없는 실패다 — 브라우저는 CORS 거부·DNS 실패·
 * 오프라인을 전부 같은 `TypeError` 로 준다. 그래서 `offline` 은 "네트워크가 끊겼다"가 아니라
 * **"응답을 받지 못했다"** 는 뜻이고, 사용자 문구도 그렇게 적어야 한다.
 */
export function classifyRpcError(err: unknown): RpcFailure {
  if (err instanceof ORPCError) {
    const raw = err.code;
    const code = typeof raw === 'string' && isErrorCode(raw) ? raw : undefined;
    const spec = code ? ERRORS[code] : undefined;
    const status = typeof err.status === 'number' ? err.status : spec?.status;
    const kind: RpcFailureKind =
      code === 'auth.unauthenticated' || status === 401
        ? 'unauthenticated'
        : code === 'auth.forbidden' || status === 403
          ? 'forbidden'
          : code
            ? 'app'
            : 'unknown';
    return {
      kind,
      code,
      status,
      // 서버가 실은 키를 먼저 쓴다. 없으면 레지스트리, 그것도 없으면 미분류 문구다 —
      // 빈 문자열을 돌려주면 화면이 **아무 말도 없이** 비어 버린다.
      messageKey:
        stringField(err.data, 'messageKey') ?? spec?.messageKey ?? ERRORS['internal.unexpected'].messageKey,
      nextActionKey: stringField(err.data, 'nextActionKey') ?? spec?.nextActionKey,
      retryable: spec?.retryable ?? false,
      details:
        typeof err.data === 'object' && err.data !== null
          ? (err.data as Record<string, unknown>).details
          : undefined,
      cause: err,
    };
  }

  if (err instanceof TypeError || (err instanceof Error && err.name === 'AbortError')) {
    const spec = ERRORS['upstream.unavailable'];
    return {
      kind: 'offline',
      code: 'upstream.unavailable',
      status: undefined,
      messageKey: spec.messageKey,
      nextActionKey: spec.nextActionKey,
      retryable: true,
      details: undefined,
      cause: err,
    };
  }

  return unexpected(err);
}

function unexpected(err: unknown): RpcFailure {
  const spec = ERRORS['internal.unexpected'];
  return {
    kind: 'unknown',
    code: undefined,
    status: undefined,
    messageKey: spec.messageKey,
    nextActionKey: spec.nextActionKey,
    retryable: false,
    details: undefined,
    cause: err,
  };
}

/*
 * ─── "응답을 받지 못했다"는 표식 — 던진 **자리**가 붙인다 ───
 * `classifyRpcError` 는 `TypeError` 를 전부 `offline` 으로 읽는다. 요청을 기다린 자리
 * (다이얼로그의 catch 등)에서는 맞다 — 거기 오는 TypeError 는 fetch 의 것이다.
 * 그런데 화면 경계에는 **컴포넌트가 던진 버그도 온다.** 렌더 중 `undefined.x` 도
 * TypeError 라서 서버가 멀쩡한데 "서버에 닿지 못했습니다"가 떴고, 네 번 밟았다
 * (2026-09-02 rpc-client 상대 경로 · 같은 날 다이얼로그의 `e.currentTarget` ·
 * 09-03 jsdom `matchMedia` · 09-23 출고 작업대가 옛 API 응답에서 `packages` 를 못 읽은 것).
 *
 * 둘은 메시지로 가를 수 없다(문자열 분기는 errors.ts 규약이 금지한다). 가를 수 있는 것은
 * **어디서 던졌나**다 — 그래서 네트워크에 닿는 자리 둘이 표식을 단다: 이 패키지의 fetch
 * (`client.ts`)와 화면 청크를 불러오는 자리(`core-web/routes.tsx`). 객체에 필드를 쓰지 않고
 * WeakSet 인 이유: 던져진 것은 남의 객체이고(브라우저의 TypeError), 표식이 직렬화·로그에
 * 섞여 나가면 안 된다.
 */
const NO_RESPONSE = new WeakSet<object>();

/** 이 실패는 응답을 못 받은 것이다 — 네트워크에 닿는 자리만 부른다. 받은 것을 그대로 돌려준다. */
export function markNoResponse<T>(err: T): T {
  if (typeof err === 'object' && err !== null) NO_RESPONSE.add(err);
  return err;
}

export function hadNoResponse(err: unknown): boolean {
  return typeof err === 'object' && err !== null && NO_RESPONSE.has(err);
}

/**
 * **요청 실패와 코드 버그가 함께 오는 자리**(화면 에러 경계)의 분류.
 *
 * `classifyRpcError` 와 한 가지만 다르다: `offline` 은 표식이 있을 때만이다. 표식 없는
 * TypeError 는 네트워크가 아니라 **이 화면의 코드**가 던진 것이고, 그때 "네트워크를
 * 확인하세요"는 사용자를 틀린 곳으로 보낸다.
 */
export function classifyBoundaryError(err: unknown): RpcFailure {
  const f = classifyRpcError(err);
  return f.kind === 'offline' && !hadNoResponse(err) ? unexpected(err) : f;
}
